import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { AppDataState } from '../../../../common/state-stores/app-data-state.state';
import { MonerisConfiguration, MonerisFailedTransactionRequest, MonerisFailedTransactionResponse, MonerisInitResponse, MonerisPaymentRequest, MonerisProcessPaymentResponse } from '../../interfaces/moneris-payment';
import { PaymentTransaction } from '../../interfaces/payment-transaction';
import { RequestUtil } from '../../../../common/util/request-util';



import { ServiceErrorUtil } from '../../../../common/util/service-error.util';
@Injectable({
  providedIn: 'root',
})
export class MonerisPaymentService {
  constructor(
    private httpClient: HttpClient,
    private appState: AppDataState
  ) {}

  /**
   * Initialize Moneris payment session and get Hosted Tokenization Profile ID
   * @returns Promise with htProfileID
   * @throws Error if initialization fails or no htProfileID received
   */
  async initializePayment(): Promise<string> {
    const requestId = RequestUtil.generateRequestId();
    try {
      const response = await lastValueFrom(
        this.httpClient.get<MonerisInitResponse>(
          `${environment.baseBackendUrl}payment/moneris/init`,
          {
            headers: {
              'App-Id': this.appState.getAppInstanceId().toString(),
              'Operator-Id': this.appState.getOperatorId().toString(),
              'PermissionSet-Id': this.appState.getPermissionSetId().toString(),
              'Request-Id': requestId
            },
          }
        )
      );

      if (!response || !response.htProfileID) {
        throw ServiceErrorUtil.createServiceError(this.constructor.name,
          'Failed to initialize payment - no htProfileID received'
        , requestId);
      }

      return response.htProfileID.trim();
    } catch (error: any) {
      throw ServiceErrorUtil.createServiceError(this.constructor.name,
        error.message || 'Payment initialization failed. Please try again.'
      , requestId);
    }
  }

  /**
   * Process payment with tokenized card data from Moneris
   * @param paymentRequest Payment details including dataKey and customer info
   * @returns Promise with payment response
   * @throws Error if payment processing fails
   */
  async processPayment(
    paymentRequest: MonerisPaymentRequest
  ): Promise<MonerisProcessPaymentResponse> {
    const requestId = RequestUtil.generateRequestId();
    try {
      const response = await lastValueFrom(
        this.httpClient.post<MonerisProcessPaymentResponse>(
          `${environment.baseBackendUrl}payment/moneris/process-payment`,
          paymentRequest,
          {
            headers: {
              'App-Id': this.appState.getAppInstanceId().toString(),
              'Operator-Id': this.appState.getOperatorId().toString(),
              'PermissionSet-Id': this.appState.getPermissionSetId().toString(),
              'Request-Id': requestId
            },
          }
        )
      );

      if (!response || !response.success) {
        throw ServiceErrorUtil.createServiceError(this.constructor.name,response?.message || 'Payment processing failed', requestId);
      }

      return response;
    } catch (error: any) {
      throw ServiceErrorUtil.createServiceError(this.constructor.name,
        error.message || 'Payment failed. Please try again.'
      , requestId);
    }
  }

  /**
   * Get Moneris payment configuration
   * @returns Promise with Moneris configuration
   * @throws Error if retrieval fails
   */
  async getConfiguration(): Promise<MonerisConfiguration> {
    const requestId = RequestUtil.generateRequestId();
    try {
      const response = await lastValueFrom(
        this.httpClient.get<MonerisConfiguration>(
          `${environment.baseBackendUrl}payment/moneris/configuration`,
          {
            headers: {
              'App-Id': this.appState.getAppInstanceId().toString(),
              'Operator-Id': this.appState.getOperatorId().toString(),
              'PermissionSet-Id': this.appState.getPermissionSetId().toString(),
              'Request-Id': requestId
            },
          }
        )
      );

      if (!response) {
        throw ServiceErrorUtil.createServiceError(this.constructor.name,'Failed to retrieve Moneris configuration', requestId);
      }

      return response;
    } catch (error: any) {
      throw ServiceErrorUtil.createServiceError(this.constructor.name,
        error.message || 'Failed to get configuration. Please try again.'
      , requestId);
    }
  }


  /**
   * Get Moneris payment configuration
   * @returns Promise with Moneris configuration
   * @throws Error if retrieval fails
   */
  async getConsumerConfiguration(): Promise<MonerisConfiguration> {
    const requestId = RequestUtil.generateRequestId();
    try {
      const response = await lastValueFrom(
        this.httpClient.get<MonerisConfiguration>(
          `${environment.baseBackendUrl}payment/moneris/consumer/configuration`,
          {
            headers: {
              'App-Id': this.appState.getAppInstanceId().toString(),
              'Operator-Id': this.appState.getOperatorId().toString(),
              'PermissionSet-Id': this.appState.getPermissionSetId().toString(),
              'Request-Id': requestId
            },
          }
        )
      );

      if (!response) {
        throw ServiceErrorUtil.createServiceError(this.constructor.name,'Failed to retrieve Moneris consumer configuration', requestId);
      }

      return response;
    } catch (error: any) {
      throw ServiceErrorUtil.createServiceError(this.constructor.name,
        error.message || 'Failed to get configuration. Please try again.'
      , requestId);
    }
  }

  /**
   * Save Moneris payment configuration
   * @param configuration Moneris configuration to save
   * @returns Promise with saved configuration
   * @throws Error if save fails
   */
  async saveConfiguration(
    configuration: MonerisConfiguration
  ): Promise<MonerisConfiguration> {
    const requestId = RequestUtil.generateRequestId();
    try {
      const response = await lastValueFrom(
        this.httpClient.post<MonerisConfiguration>(
          `${environment.baseBackendUrl}payment/moneris/configuration`,
          configuration,
          {
            headers: {
              'App-Id': this.appState.getAppInstanceId().toString(),
              'Operator-Id': this.appState.getOperatorId().toString(),
              'PermissionSet-Id': this.appState.getPermissionSetId().toString(),
              'Request-Id': requestId
            },
          }
        )
      );

      if (!response) {
        throw ServiceErrorUtil.createServiceError(this.constructor.name,'Failed to save Moneris configuration', requestId);
      }

      return response;
    } catch (error: any) {
      throw ServiceErrorUtil.createServiceError(this.constructor.name,
        error.message || 'Failed to save configuration. Please try again.'
      , requestId);
    }
  }

  /**
   * Get Moneris payment transactions
   * @returns Promise with list of payment transactions
   * @throws Error if retrieval fails
   */
  async getTransactions(): Promise<PaymentTransaction[]> {
    const requestId = RequestUtil.generateRequestId();
    try {
      const response = await lastValueFrom(
        this.httpClient.get<PaymentTransaction[]>(
          `${environment.baseBackendUrl}payment/moneris/transactions`,
          {
            headers: {
              'App-Id': this.appState.getAppInstanceId().toString(),
              'Operator-Id': this.appState.getOperatorId().toString(),
              'PermissionSet-Id': this.appState.getPermissionSetId().toString(),
              'Request-Id': requestId
            },
          }
        )
      );

      if (!response) {
        throw ServiceErrorUtil.createServiceError(this.constructor.name, 'Failed to retrieve payment transactions', requestId);
      }

      return response;
    } catch (error: any) {
      throw ServiceErrorUtil.createServiceError(
        this.constructor.name,
        error.message || 'Failed to get transactions. Please try again.',
        requestId
      );
    }
  }

  /**
   * Register a failed Moneris payment transaction
   * @param request Failed transaction details
   * @returns Promise with registration response
   * @throws Error if registration fails
   */
  async registerFailedTransaction(
    request: MonerisFailedTransactionRequest
  ): Promise<MonerisFailedTransactionResponse> {
    const requestId = RequestUtil.generateRequestId();
    try {
      const response = await lastValueFrom(
        this.httpClient.post<MonerisFailedTransactionResponse>(
          `${environment.baseBackendUrl}payment/moneris/register-failed-transaction`,
          request,
          {
            headers: {
              'App-Id': this.appState.getAppInstanceId().toString(),
              'Operator-Id': this.appState.getOperatorId().toString(),
              'PermissionSet-Id': this.appState.getPermissionSetId().toString(),
              'Request-Id': requestId
            },
          }
        )
      );

      if (!response || !response.success) {
        throw ServiceErrorUtil.createServiceError(
          this.constructor.name,
          response?.message || 'Failed to register failed transaction',
          requestId
        );
      }

      return response;
    } catch (error: any) {
      throw ServiceErrorUtil.createServiceError(
        this.constructor.name,
        error.message || 'Failed to register failed transaction. Please try again.',
        requestId
      );
    }
  }
}

